import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";

/**
 * Hosts page-based CRUD children in one outlet.
 * Removes zombie hosts left when Angular deactivates a view but the DOM
 * node stays (duplicate #main-content / incomplete destroy).
 */
@Component({
  selector: "app-parametrage-child-outlet",
  template: `<router-outlet
    (activate)="onActivate($event)"
    (deactivate)="onDeactivate()"
  ></router-outlet>`,
})
export class ParametrageChildOutletComponent
  implements AfterViewInit, OnDestroy {
  @ViewChild(RouterOutlet, { static: false }) outlet: RouterOutlet;

  private subs = new Subscription();
  private activeComponent: any = null;
  private pruneTimer: any = null;

  constructor(private el: ElementRef, private router: Router) {}

  ngAfterViewInit() {
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe(() => this.schedulePrune())
    );
    this.schedulePrune();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    if (this.pruneTimer != null) {
      clearTimeout(this.pruneTimer);
    }
  }

  onActivate(component: any): void {
    this.activeComponent = component;
    this.schedulePrune();
  }

  onDeactivate(): void {
    this.schedulePrune();
  }

  private schedulePrune(): void {
    if (this.pruneTimer != null) {
      clearTimeout(this.pruneTimer);
    }
    // After Angular finishes inserting/removing views.
    this.pruneTimer = setTimeout(() => {
      this.pruneTimer = null;
      this.pruneZombies();
    }, 0);
  }

  private pruneZombies(): void {
    const host = this.el.nativeElement as HTMLElement;
    const components = Array.from(host.children).filter(
      (k: Element) => k.tagName !== "ROUTER-OUTLET"
    ) as HTMLElement[];

    if (components.length <= 1) {
      return;
    }

    let keepIndex = 0;
    const ng = (window as any).ng;
    if (this.activeComponent && ng && typeof ng.probe === "function") {
      const idx = components.findIndex((el) => {
        try {
          const probe = ng.probe(el);
          return probe && probe.componentInstance === this.activeComponent;
        } catch {
          return false;
        }
      });
      if (idx >= 0) {
        keepIndex = idx;
      }
    }

    components.forEach((node, i) => {
      if (i !== keepIndex && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }
}
